/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import moment from "moment"
import { Exam, examPatientOf } from "@medaica/common/types"

export type ExamDemographics = {
  /** The patient's name, masked for a viewer from another organization */
  patientName: string
  dob?: string
  age?: string
  sex?: string
  examDate: string
}

const sexAbbreviation = (sex: string | null | undefined): string | undefined => {
  if (sex === "male") return "M"
  if (sex === "female") return "F"
  return undefined
}

/**
 * The identity facts the Medaica Viewer and Medaica Report pages show for an
 * exam: the patient the exam was for (frozen at booking when it was booked),
 * not the shared, editable profile. The profile's organization decides the
 * cross-organization mask on the name.
 */
export const examDemographics = (exam: Exam, viewerOrganizationId: string): ExamDemographics => {
  const patient = examPatientOf(exam)
  return {
    patientName: viewerOrganizationId === exam.patientProfile.organizationId ? patient.fullName : "**********",
    dob: patient.birthdate ? moment(patient.birthdate).format("ll") : undefined,
    // Whole years, as the standalone viewer and report show it
    age: patient.birthdate ? String(moment().diff(moment(patient.birthdate), "years")) : undefined,
    sex: sexAbbreviation(patient.sex),
    // The exam's true date (the prototype shows today's date there)
    examDate: moment(exam.dateCreated).format("MMM D, YYYY"),
  }
}
